import React, { useEffect, useState } from "react";
import { Button, Card, Row, Col, Spinner, Modal, Alert } from "react-bootstrap";
import authConfig from "../../authConfig";
import FormRenderer from "./FormRenderer";

/*
  FormSubmission
  - Lists available member forms (cookie auth) and shows an "Open" button for each.
  - Clicking "Open" opens a modal containing FormRenderer for that formId.
  - When FormRenderer calls onSubmitted the modal closes and a summary modal is shown.
*/
export default function FormSubmission(props) {
    const [forms, setForms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [showModal, setShowModal] = useState(false);
    const [activeFormId, setActiveFormId] = useState(null);

    // summary modal state
    const [showSummaryModal, setShowSummaryModal] = useState(false);
    const [submissionSummary, setSubmissionSummary] = useState(null);

    const [successMessage, setSuccessMessage] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await fetch(authConfig.api.urls.getMemberFormListForMember, {
                    credentials: "include",
                });
                if (!res.ok) throw new Error(`Failed to load forms: ${res.status} ${res.statusText}`);
                const data = await res.json();
                const mapped = (data || []).map((o) => ({
                    id: o.Id || o.id,
                    name: o.Name || o.name || "(no name)",
                }));
                if (!cancelled) setForms(mapped);
            } catch (ex) {
                console.error(ex);
                if (!cancelled) setError(ex?.message || "Error loading forms");
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, []);

    const handleOpen = (formId) => {
        setActiveFormId(formId);
        setShowModal(true);
        setSuccessMessage(null);
        setSubmissionSummary(null);
        setShowSummaryModal(false);
    };

    const handleClose = () => {
        setShowModal(false);
        setActiveFormId(null);
    };

    // now accepts a submission object from FormRenderer
    const handleSubmitted = (submission) => {
        // close the form modal and show a summary modal with the submission details
        setShowModal(false);
        setActiveFormId(null);
        setSubmissionSummary(submission || null);
        setShowSummaryModal(true);
        setSuccessMessage("Form submitted successfully");
        if (props.onSubmit) {
            props.onSubmit();
        }
        // optionally clear success message after a short while while leaving summary visible
        setTimeout(() => setSuccessMessage(null), 4000);
    };

    const closeSummary = () => {
        setShowSummaryModal(false);
        setSubmissionSummary(null);
    };

    // Helper to render submission values with labels when schema is available
    const renderSummaryRows = (submission) => {
        if (!submission) return null;
        const { schema = [], values = {} } = submission;
        // Build map name -> label from schema
        const labelMap = {};
        (schema || []).forEach(f => {
            if (f && f.name) labelMap[f.name] = f.label || f.name;
        });

        const keys = Object.keys(values || {});
        if (keys.length === 0) {
            return <div>No submission values.</div>;
        }

        return (
            <div>
                {keys.map((k) => {
                    const raw = values[k];
                    let display;
                    if (raw === null || raw === undefined || raw === "") display = "";
                    else if (typeof raw === "boolean") display = raw ? "Yes" : "No";
                    else if (typeof raw === "object") display = <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{JSON.stringify(raw, null, 2)}</pre>;
                    else display = String(raw);

                    return (
                        <Row key={k} className="mb-2">
                            <Col xs={4} className="text-muted">{labelMap[k] || k}</Col>
                            <Col xs={8}>{display}</Col>
                        </Row>
                    );
                })}
            </div>
        );
    };

    return (
        <><div>
            {successMessage && <Alert variant="success">{successMessage}</Alert>}
            {error && <Alert variant="danger">{error}</Alert>}
            {loading ? (
                <div className="text-center"><Spinner animation="border" /></div>
            ) : (
                <>
                    {forms.length === 0 ? (
                        <div>No forms available.</div>
                    ) : (
                        forms.map((f, idx) => (
                            <Row key={f.id || idx} className="align-items-center mb-2">
                                <Col xs={8}>{f.name}</Col>
                                <Col xs={4} className="text-end">
                                    <Button size="sm" variant="success" onClick={() => handleOpen(f.id)}>
                                        Open
                                    </Button>
                                </Col>
                            </Row>
                        ))
                    )}
                </>
            )}
        </div>

            <Modal show={showModal} onHide={handleClose} size="xl" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Complete Form</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {activeFormId ? (
                        <FormRenderer formId={activeFormId} onSubmitted={handleSubmitted} />
                    ) : (
                        <div className="text-center"><Spinner animation="border" /></div>
                    )}
                </Modal.Body>
                {/*<Modal.Footer>*/}
                {/*  <Button variant="secondary" onClick={handleClose}>Close</Button>*/}
                {/*</Modal.Footer>*/}
            </Modal>

            {/* Summary modal shown after a successful submission */}
            <Modal show={showSummaryModal} onHide={closeSummary} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Submission Summary</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {submissionSummary ? (
                        <>
                            <h5 className="mb-3">{submissionSummary.heading || "Submitted Form"}</h5>
                            {renderSummaryRows(submissionSummary)}
                        </>
                    ) : (
                        <div>No summary available.</div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={closeSummary}>Close</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}